import { SphereMark } from "@/components/brand/SphereMark";

const BOOKING_URL = process.env.REACT_APP_BOOKING_URL || null;

const RANK_LABEL = ["Primary opportunity", "Secondary opportunity", "Supporting opportunity"];
const RANK_COLOR = ["#1408F7", "#000000", "#71717A"];
const STATUS_STYLE = {
  Priority: { bg: "#1408F7", color: "#fff", border: "#1408F7" },
  Opportunity: { bg: "#111111", color: "#fff", border: "#111111" },
  Developing: { bg: "#fff", color: "#525252", border: "#d4d4d4" },
  "Strong foundation": { bg: "#fff", color: "#a1a1aa", border: "#e5e5e5" },
};

function BrandBar() {
  return (
    <div className="flex items-center justify-between border-b border-neutral-200 pb-3">
      <div className="flex items-center gap-2">
        <SphereMark size={9} />
        <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-neutral-900">
          Three Words Full
        </span>
      </div>
      <span className="text-[9px] font-semibold uppercase tracking-[0.24em] text-neutral-400">
        B2B Marketing Diagnostic
      </span>
    </div>
  );
}

function FootBar() {
  return (
    <div className="pdf-flush border-t border-neutral-200 pt-3">
      <p className="text-[9px] font-semibold uppercase tracking-[0.24em] text-neutral-400">
        Three Words Full · Strategy. Systems. People.
      </p>
    </div>
  );
}

export function ResultsPrint({ data }) {
  if (!data?.results) return null;
  const r = data.results;
  const about = data.about || {};
  const primary = r.primary_archetype || r.dual_archetypes?.[0];
  const secondary = r.secondary_archetype || r.dual_archetypes?.[1];
  const showSecondary = r.secondary_display || r.archetype_state === "dual";
  const ssp = r.ssp_normalised || {};
  const ranked = r.ssp_ranked || [];
  const dimRanked = r.dimension_ranked || [];
  const paras = r.interpretation?.paragraphs || [];
  const pathwayLabel = data.pathway === "marketing" ? "Marketing pathway" : "Leadership pathway";

  return (
    <div className="pdf-doc" data-testid="results-pdf-doc">
      {/* ---------------- PAGE 1 — YOUR DIAGNOSTIC (cover) ---------------- */}
      <section className="pdf-page">
        <BrandBar />
        <div className="pdf-block flex flex-1 flex-col justify-center py-8 text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#1408F7]">
            {pathwayLabel}
          </p>
          <h1 className="mt-3 text-[34px] font-bold leading-tight tracking-tight text-neutral-900">
            Your Diagnostic
          </h1>
          {about.name && <p className="mt-2 text-lg text-neutral-800">{about.name}</p>}
          {(about.job_title || about.organisation) && (
            <p className="mt-1 text-sm text-neutral-500">
              {[about.job_title, about.organisation].filter(Boolean).join(" · ")}
            </p>
          )}

          {primary?.image && (
            <div className="mt-7 flex justify-center">
              <img
                src={primary.image}
                alt={`${primary.name} illustration`}
                style={{ maxHeight: "92mm", width: "auto", maxWidth: "78%" }}
                className="rounded-sm border border-neutral-200 object-contain"
              />
            </div>
          )}

          <p className="mt-7 text-[11px] font-semibold uppercase tracking-[0.24em] text-neutral-400">
            Your primary archetype
          </p>
          <h2 className="mt-2 text-[30px] font-bold tracking-tight text-neutral-900">
            {primary?.name}
          </h2>
          {primary?.hero && (
            <p className="mx-auto mt-3 max-w-[130mm] text-base font-medium leading-snug text-neutral-700">
              {primary.hero}
            </p>
          )}
        </div>
        <FootBar />
      </section>

      {/* ---------------- PAGE 2 — YOUR ARCHETYPE ---------------- */}
      <section className="pdf-page">
        <BrandBar />
        <div className="py-6">
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-neutral-400">
            Your archetype
          </p>
          <h2 className="mt-2 text-[26px] font-bold tracking-tight text-neutral-900">
            {primary?.name}
          </h2>

          {primary?.profile && (
            <p className="pdf-block mt-4 text-[13px] leading-relaxed text-neutral-700">
              {primary.profile}
            </p>
          )}

          {primary?.opportunity && (
            <div
              className="pdf-block mt-5 border-l-2 p-4"
              style={{ borderColor: "#1408F7", background: "rgba(20,8,247,0.06)" }}
            >
              <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#1408F7]">
                Your opportunity
              </p>
              <p className="mt-1.5 text-[13px] font-medium text-neutral-900">{primary.opportunity}</p>
            </div>
          )}

          {primary?.signature && (
            <p className="pdf-block mt-6 text-[22px] font-bold tracking-tight text-neutral-900">
              {primary.signature}
            </p>
          )}

          {primary?.watch_for?.length > 0 && (
            <div className="pdf-block mt-6 border border-neutral-200 p-5">
              <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-neutral-400">
                Watch for
              </p>
              <ul className="mt-3 space-y-1.5 text-[12px] text-neutral-700">
                {primary.watch_for.map((c) => (
                  <li key={c} className="flex gap-2">
                    <span className="mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-neutral-900" />
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {showSecondary && secondary && (
            <div className="pdf-block mt-5 border border-neutral-200 p-5">
              <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-neutral-400">
                Secondary influence
              </p>
              <p className="mt-1 text-base font-semibold text-neutral-900">{secondary.name}</p>
              {secondary.hero && <p className="mt-1 text-[12px] text-neutral-600">{secondary.hero}</p>}
            </div>
          )}
        </div>
        <FootBar />
      </section>

      {/* ---------------- PAGE 3 — WHERE THE OPPORTUNITY SITS ---------------- */}
      <section className="pdf-page">
        <BrandBar />
        <div className="py-6">
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-neutral-400">
            The framework
          </p>
          <h2 className="mt-2 text-[26px] font-bold tracking-tight text-neutral-900">
            Where the opportunity sits.
          </h2>

          <div className="mt-5 space-y-3">
            {ranked.map((area, i) => {
              const score = ssp[area] ?? 0;
              const width = 45 + Math.round((score / 100) * 50);
              return (
                <div key={area} className="pdf-block border border-neutral-200 p-4">
                  <div className="flex items-center gap-4">
                    <span className="text-xl font-bold tabular-nums" style={{ color: RANK_COLOR[i] }}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="flex-1">
                      <div className="flex items-baseline justify-between">
                        <span className="text-base font-semibold text-neutral-900">{area}</span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-neutral-400">
                          {RANK_LABEL[i]}
                        </span>
                      </div>
                      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-sm bg-neutral-100">
                        <div
                          className="h-full rounded-sm"
                          style={{ width: `${width}%`, background: RANK_COLOR[i] }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Your Three — strong visual moment, kept intact */}
          <div className="pdf-block mt-7 p-8" style={{ background: "#000000", color: "#fff" }}>
            {r.your_three_state === "strong_foundation" ? (
              <>
                <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-neutral-500">
                  Your result
                </p>
                <h3 className="mt-2 text-[30px] font-bold tracking-tight text-white">
                  Strong Foundation.
                </h3>
                <p className="mt-3 max-w-[120mm] text-sm text-neutral-300">
                  No single dimension reached the threshold for priority attention. Your marketing shows
                  a strong, balanced foundation across the framework.
                </p>
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold text-white">Your Three</h3>
                <p className="mt-2 max-w-[120mm] text-sm text-neutral-400">
                  Based on your answers, these are the things that matter most right now.
                </p>
                <div className="mt-6 space-y-2">
                  {(r.your_three || []).map((item, i) => (
                    <div key={item.dimension} className="flex items-baseline gap-4">
                      <span className="text-sm font-medium tabular-nums" style={{ color: "#1408F7" }}>
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="text-[34px] font-bold uppercase leading-none tracking-tight text-white">
                        {item.dimension}.
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
        <FootBar />
      </section>

      {/* ---------------- PAGE 4 — THE DETAILED PICTURE ---------------- */}
      <section className="pdf-page">
        <BrandBar />
        <div className="py-6">
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-neutral-400">
            Six dimensions
          </p>
          <h2 className="mt-2 text-[26px] font-bold tracking-tight text-neutral-900">
            The detailed picture
          </h2>
          <p className="mt-3 max-w-[130mm] text-[12px] leading-relaxed text-neutral-500">
            The framework broken down. This supports your result — the priorities above are what to act
            on first.
          </p>

          <div className="mt-5 border border-neutral-200">
            {dimRanked.map((dim) => {
              const label = r.dimension_status?.[dim] || "Not assessed";
              const s = STATUS_STYLE[label] || STATUS_STYLE["Strong foundation"];
              return (
                <div
                  key={dim}
                  className="pdf-block flex items-center gap-4 border-b border-neutral-200 p-4 last:border-b-0"
                >
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-neutral-900">{dim}</h3>
                    <p className="mt-0.5 text-[11px] text-neutral-500">
                      {r.dimension_descriptors?.[dim]}
                    </p>
                  </div>
                  <span
                    className="flex-shrink-0 rounded-sm border px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.18em]"
                    style={{ background: s.bg, color: s.color, borderColor: s.border }}
                  >
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        <FootBar />
      </section>

      {/* ---------------- PAGE 5 — WHAT THIS MEANS + TALK IT THROUGH ---------------- */}
      <section className="pdf-page">
        <BrandBar />
        <div className="py-6">
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-neutral-400">
            Interpretation
          </p>
          <h2 className="mt-2 text-[26px] font-bold tracking-tight text-neutral-900">
            What this means
          </h2>

          {paras.length > 0 && (
            <div className="mt-4 space-y-3">
              {paras.map((p, i) =>
                p.emphasis ? (
                  <p
                    key={i}
                    className="pdf-block border-l-2 pl-4 text-[13px] font-medium leading-relaxed text-neutral-900"
                    style={{ borderColor: "#1408F7" }}
                  >
                    {p.text}
                  </p>
                ) : (
                  <p key={i} className="pdf-block text-[13px] leading-relaxed text-neutral-700">
                    {p.text}
                  </p>
                )
              )}
            </div>
          )}

          {/* Talk it through — flows beneath interpretation, kept intact */}
          <div className="pdf-block mt-7 p-7" style={{ background: "#000000", color: "#fff" }}>
            <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-neutral-500">
              Talk it through
            </p>
            <h3 className="mt-2 text-[22px] font-bold tracking-tight text-white">
              Your results are a starting point.
            </h3>
            <p className="mt-3 max-w-[135mm] text-[12px] leading-relaxed text-neutral-300">
              A diagnostic can show where the signals are. The next step is understanding what&apos;s
              causing them. Book a conversation with Three Words Full to talk through your results and
              explore what they could mean for your organisation.
            </p>
            {BOOKING_URL && (
              <a
                href={BOOKING_URL}
                className="mt-5 inline-block px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.16em]"
                style={{ background: "#1408F7", color: "#fff" }}
              >
                Book a results conversation →
              </a>
            )}
          </div>
        </div>
        <FootBar />
      </section>
    </div>
  );
}
